import { gsap } from 'gsap';
import { Physics2DPlugin } from 'gsap/Physics2DPlugin';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin(Physics2DPlugin, InertiaPlugin);
let _hitEmitter = null;
let _hitEmitterKey = '';
let _stageIntroTl = null;

const DEFAULT_EMITTER_CONFIG = {
  emitterSize: 5,
  dotQuantity: 25,
  dotSizeMin: 3,
  dotSizeMax: 5,
  speed: 1,
  gravity: 0,
  colorA: '#ff8a52',
  colorB: '#ffd38f',
};

function _resetDotStyles(dot, conf) {
  const angle = Math.random() * Math.PI * 2;
  const size = gsap.utils.random(conf.dotSizeMin, conf.dotSizeMax, 1);
  const length = Math.random() * (conf.emitterSize * 0.5 - size * 0.5);
  const x = Math.cos(angle) * length;
  const y = Math.sin(angle) * length;

  gsap.set(dot.el, {
    x,
    y,
    width: size,
    height: size,
    xPercent: -50,
    yPercent: -50,
    opacity: 1,
    rotation: 0,
    force3D: true,
  });

  dot.angleDeg = (angle * 180) / Math.PI;
  dot.velocity = gsap.utils.random(100, 350, 1) * conf.speed;
  dot.life = gsap.utils.random(0.75, 1.35, 0.01);
  dot.spinVelocity = gsap.utils.random(-760, 760, 1);
}

function _buildHitEmitter(opts = {}) {
  const conf = { ...DEFAULT_EMITTER_CONFIG, ...opts };
  const parent = document.getElementById('app') || document.body;
  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;left:0;top:0;overflow:visible;z-index:9;pointer-events:none;';
  parent.appendChild(container);

  const dots = [];
  for (let i = 0; i < conf.dotQuantity; i++) {
    const dot = document.createElement('div');
    dot.style.position = 'absolute';
    dot.style.borderRadius = '50%';
    dot.style.background = i % 2 === 0 ? conf.colorA : conf.colorB;
    dot.style.boxShadow = `0 0 10px ${conf.colorA}`;
    container.appendChild(dot);
    dots.push({ el: dot, angleDeg: 0, velocity: 0, life: 1, spinVelocity: 0 });
  }

  const tl = gsap.timeline({ paused: true });
  for (const dot of dots) {
    _resetDotStyles(dot, conf);
    tl.to(dot.el, {
      physics2D: {
        angle: dot.angleDeg,
        velocity: dot.velocity,
        gravity: 500 * conf.gravity,
      },
      duration: dot.life,
      overwrite: 'auto',
    }, 0)
      .to(dot.el, {
        opacity: 0,
        duration: 0.22,
        ease: 'power2.inOut',
        overwrite: 'auto',
      }, Math.max(0.2, dot.life * 0.58))
      .to(dot.el, {
        inertia: {
          rotation: { velocity: dot.spinVelocity, resistance: 190 },
        },
        overwrite: 'auto',
      }, 0);
  }

  return { conf, container, dots, tl };
}

function _disposeHitEmitter(emitter) {
  if (!emitter) return;
  emitter.tl?.kill();
  emitter.container?.remove();
}

function _ensureHitEmitter(opts = {}) {
  const key = JSON.stringify({ ...DEFAULT_EMITTER_CONFIG, ...opts });
  if (!_hitEmitter || key !== _hitEmitterKey) {
    _disposeHitEmitter(_hitEmitter);
    _hitEmitter = _buildHitEmitter(opts);
    _hitEmitterKey = key;
  }
  return _hitEmitter;
}

function finishOverlayHide(el) {
  el.classList.remove('visible');
  el.classList.add('hidden');
}

export function showOverlay(el, opts = {}) {
  if (!el) return null;
  const { duration = 0.22, ease = 'power2.out' } = opts;
  gsap.killTweensOf(el);
  el.classList.remove('hidden');
  el.classList.add('visible');
  return gsap.fromTo(
    el,
    { autoAlpha: 0 },
    { autoAlpha: 1, duration, ease, overwrite: 'auto' }
  );
}

export function hideOverlay(el, opts = {}) {
  if (!el) return null;
  const { duration = 0.2, ease = 'power2.in' } = opts;
  gsap.killTweensOf(el);
  return gsap.to(el, {
    autoAlpha: 0,
    duration,
    ease,
    overwrite: 'auto',
    onComplete: () => finishOverlayHide(el),
  });
}

export function runBlackoutTransition(blackoutEl, opts = {}) {
  if (!blackoutEl) return Promise.resolve();
  const {
    fadeOutDuration = 0.3,
    fadeInDuration = 0.3,
    holdDuration = 0.02,
    onBlack = null,
  } = opts;
  gsap.killTweensOf(blackoutEl);
  blackoutEl.style.pointerEvents = 'none';
  blackoutEl.style.opacity = '0';
  return new Promise((resolve) => {
    const tl = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => resolve(),
    });
    tl.to(blackoutEl, {
      autoAlpha: 1,
      duration: Math.max(0, fadeOutDuration),
      ease: 'power2.inOut',
    }).add(async () => {
      if (typeof onBlack === 'function') await onBlack();
    });
    tl.to({}, { duration: Math.max(0, holdDuration) });
    tl.to(blackoutEl, {
      autoAlpha: 0,
      duration: Math.max(0, fadeInDuration),
      ease: 'power2.inOut',
    });
  });
}

function typeLine(lineEl, text, duration = 0.8, opts = {}) {
  if (!lineEl) return Promise.resolve();
  const { skipState = null } = opts;
  const source = String(text || '');
  lineEl.textContent = '';
  if (!source.length || duration <= 0) {
    lineEl.textContent = source;
    return Promise.resolve();
  }
  const state = { chars: 0 };
  return new Promise((resolve) => {
    let done = false;
    const finishNow = (tween) => {
      if (done) return;
      done = true;
      lineEl.textContent = source;
      gsap.ticker.remove(tickSkip);
      tween?.kill?.();
      resolve();
    };
    const tween = gsap.to(state, {
      chars: source.length,
      duration,
      ease: 'none',
      overwrite: 'auto',
      onUpdate: () => {
        if (skipState?.requested) {
          finishNow(tween);
          return;
        }
        lineEl.textContent = source.slice(0, Math.floor(state.chars));
      },
      onComplete: () => {
        if (!done) {
          done = true;
          lineEl.textContent = source;
          gsap.ticker.remove(tickSkip);
          resolve();
        }
      },
    });
    const tickSkip = () => {
      if (!skipState?.requested) return;
      finishNow(tween);
    };
    gsap.ticker.add(tickSkip);
  });
}

function waitSeconds(duration = 0, skipState = null) {
  const safeDuration = Math.max(0, Number(duration) || 0);
  if (safeDuration <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const complete = (tween) => {
      if (done) return;
      done = true;
      gsap.ticker.remove(tickSkip);
      tween?.kill?.();
      resolve();
    };
    const tween = gsap.to({}, {
      duration: safeDuration,
      ease: 'none',
      onComplete: () => complete(),
    });
    const tickSkip = () => {
      if (!skipState?.requested) return;
      complete(tween);
    };
    gsap.ticker.add(tickSkip);
  });
}

export async function runLoreIntroTransition(containerEl, lines = [], opts = {}) {
  if (!containerEl) return;
  const lineEls = [...containerEl.querySelectorAll('.run-lore-line')];
  const safeLines = lines.slice(0, lineEls.length).map((line) => String(line || ''));
  const {
    fadeInDuration = 0.15,
    fadeOutDuration = 0.35,
    lineDurations = [1.05, 1.15, 0.8],
    lineGap = 0.16,
    holdDuration = 0.85,
  } = opts;
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const skipState = { requested: false };
  const onPointerDown = (ev) => {
    if (ev?.button !== 0) return;
    skipState.requested = true;
  };

  gsap.killTweensOf(containerEl);
  lineEls.forEach((el) => gsap.killTweensOf(el));
  lineEls.forEach((el, idx) => {
    el.textContent = '';
    el.style.opacity = '0';
  });

  containerEl.classList.remove('hidden');
  containerEl.setAttribute('aria-hidden', 'false');
  containerEl.style.pointerEvents = 'none';
  containerEl.style.opacity = '0';
  window.addEventListener('pointerdown', onPointerDown, { capture: true });

  try {
    await gsap.to(containerEl, {
      autoAlpha: 1,
      duration: Math.max(0, fadeInDuration),
      ease: 'power1.out',
      overwrite: 'auto',
    });

    if (reduceMotion) {
      lineEls.forEach((el, idx) => {
        el.textContent = safeLines[idx] || '';
        el.style.opacity = '1';
      });
      await waitSeconds(1.45, skipState);
    } else {
      for (let i = 0; i < lineEls.length; i += 1) {
        const el = lineEls[i];
        const text = safeLines[i] || '';
        const duration = Number.isFinite(lineDurations[i]) ? lineDurations[i] : 0.9;
        el.style.opacity = '1';
        await typeLine(el, text, skipState.requested ? 0.06 : Math.max(0, duration), { skipState });
        if (i < lineEls.length - 1) {
          await waitSeconds(skipState.requested ? 0.03 : Math.max(0, lineGap), skipState);
        }
      }
      await waitSeconds(skipState.requested ? 0.08 : Math.max(0, holdDuration), skipState);
    }

    await gsap.to(containerEl, {
      autoAlpha: 0,
      duration: skipState.requested ? 0.14 : Math.max(0, fadeOutDuration),
      ease: 'power2.inOut',
      overwrite: 'auto',
    });
  } finally {
    window.removeEventListener('pointerdown', onPointerDown, { capture: true });
    containerEl.classList.add('hidden');
    containerEl.setAttribute('aria-hidden', 'true');
  }
}

export function setBarValue(el, progress, opts = {}) {
  if (!el) return null;
  const { duration = 0.16, ease = 'power2.out' } = opts;
  const target = Math.max(0, Math.min(1, progress)) * 100;
  gsap.killTweensOf(el);
  return gsap.to(el, {
    width: `${target}%`,
    duration,
    ease,
    overwrite: 'auto',
  });
}

export function pulseValue(el, opts = {}) {
  if (!el) return null;
  const { scale = 1.06, duration = 0.18 } = opts;
  gsap.killTweensOf(el);
  return gsap.fromTo(
    el,
    { scale },
    { scale: 1, duration, ease: 'power2.out', overwrite: 'auto' }
  );
}

export function fragmentCardHoverIn(el, opts = {}) {
  if (!el) return null;
  const { scale = 1.07, lift = 10, duration = 0.32 } = opts;
  gsap.killTweensOf(el);
  return gsap.to(el, {
    scale,
    y: -lift,
    duration,
    ease: 'back.out(2.6)',
    overwrite: 'auto',
  });
}

export function fragmentCardHoverOut(el, opts = {}) {
  if (!el) return null;
  const { duration = 0.22 } = opts;
  gsap.killTweensOf(el);
  return gsap.to(el, {
    scale: 1,
    y: 0,
    duration,
    ease: 'power2.out',
    overwrite: 'auto',
  });
}

export function animateDraftHeader(metaEl, titleEl, subEl) {
  if (!metaEl || !titleEl) return null;
  gsap.killTweensOf(metaEl);
  gsap.killTweensOf(titleEl);
  if (subEl) gsap.killTweensOf(subEl);

  gsap.set(metaEl, { autoAlpha: 0, y: -26, scale: 0.72 });
  gsap.set(titleEl, { autoAlpha: 0, y: 30, scale: 0.64 });
  if (subEl) gsap.set(subEl, { autoAlpha: 0, y: 12 });

  const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
  tl.to(metaEl, {
    autoAlpha: 1,
    y: 0,
    scale: 1,
    duration: 0.32,
    ease: 'back.out(2.5)',
  }, 0)
    .to(metaEl, {
      scale: 0.98,
      duration: 0.08,
      ease: 'power2.out',
      yoyo: true,
      repeat: 1,
    }, 0.32)
    .to(titleEl, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      duration: 0.38,
      ease: 'back.out(3.1)',
    }, 0.04)
    .to(titleEl, {
      scale: 0.985,
      duration: 0.1,
      ease: 'power2.out',
      yoyo: true,
      repeat: 1,
    }, 0.4);

  if (subEl) {
    tl.to(subEl, {
      autoAlpha: 1,
      y: 0,
      duration: 0.16,
      ease: 'power2.out',
    }, 0.14);
  }

  return tl;
}

export function fragmentChipEnter(el, opts = {}) {
  if (!el) return null;
  const { delay = 0 } = opts;
  gsap.killTweensOf(el);
  return gsap.fromTo(
    el,
    { autoAlpha: 0, y: 10, scale: 0.94 },
    {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      delay,
      duration: 0.2,
      ease: 'power2.out',
      overwrite: 'auto',
    }
  );
}

export function toastInOut(el, holdMs = 1400) {
  if (!el) return null;
  gsap.killTweensOf(el);
  el.classList.remove('hidden');
  const holdSec = Math.max(0, holdMs) / 1000;
  return gsap.timeline({
    defaults: { overwrite: 'auto' },
    onComplete: () => el.classList.add('hidden'),
  })
    .fromTo(el, { autoAlpha: 0, y: -10 }, { autoAlpha: 1, y: 0, duration: 0.18, ease: 'power2.out' })
    .to(el, { autoAlpha: 1, y: 0, duration: holdSec, ease: 'none' })
    .to(el, { autoAlpha: 0, y: -6, duration: 0.18, ease: 'power2.in' });
}

export function stageIntroInOut(containerEl, mainEl, subEl, opts = {}) {
  if (!containerEl || !mainEl || !subEl) return null;
  const { holdMs = 1150 } = opts;
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  _stageIntroTl?.kill();
  gsap.killTweensOf([containerEl, mainEl, subEl]);

  containerEl.setAttribute('aria-hidden', 'false');
  containerEl.classList.add('visible');
  containerEl.classList.remove('hidden');

  if (reduceMotion) {
    _stageIntroTl = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => containerEl.setAttribute('aria-hidden', 'true'),
    })
      .fromTo(containerEl, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.08, ease: 'none' })
      .to({}, { duration: Math.max(0.15, holdMs / 1000), ease: 'none' })
      .to(containerEl, { autoAlpha: 0, duration: 0.1, ease: 'none' });
    return _stageIntroTl;
  }

  gsap.set(mainEl, { x: -28, autoAlpha: 0, scale: 0.96, skewX: -11 });
  gsap.set(subEl, { x: 22, autoAlpha: 0, scale: 0.96, skewX: -7 });

  _stageIntroTl = gsap.timeline({
    defaults: { overwrite: 'auto' },
    onComplete: () => containerEl.setAttribute('aria-hidden', 'true'),
  })
    .fromTo(containerEl, { autoAlpha: 0, y: -12 }, {
      autoAlpha: 1,
      y: 0,
      duration: 0.26,
      ease: 'power2.out',
    }, 0)
    .to(mainEl, {
      x: 0,
      autoAlpha: 1,
      scale: 1,
      skewX: -9,
      duration: 0.38,
      ease: 'power3.out',
    }, 0.02)
    .to(subEl, {
      x: 0,
      autoAlpha: 1,
      scale: 1,
      skewX: -2,
      duration: 0.32,
      ease: 'power2.out',
    }, 0.08)
    .to({}, { duration: Math.max(0.2, holdMs / 1000), ease: 'none' })
    .to(containerEl, {
      autoAlpha: 0,
      y: -8,
      duration: 0.22,
      ease: 'power2.in',
    });

  return _stageIntroTl;
}

export function animateHitFlash(el) {
  if (!el) return null;
  gsap.killTweensOf(el);
  return gsap.fromTo(
    el,
    { autoAlpha: 0.8 },
    { autoAlpha: 0, duration: 0.14, ease: 'power1.out', overwrite: 'auto' }
  );
}

export function animateRingFlash(mesh, material) {
  if (!mesh || !material) return null;
  gsap.killTweensOf(mesh.scale);
  gsap.killTweensOf(material);
  mesh.scale.setScalar(1);
  return gsap.timeline({
    defaults: { overwrite: 'auto' },
    onComplete: () => mesh.parent?.remove(mesh),
  })
    .to(mesh.scale, { x: 1.8, y: 1.8, z: 1.8, duration: 0.36, ease: 'power2.out' }, 0)
    .to(material, { opacity: 0, duration: 0.36, ease: 'power2.out' }, 0);
}

export function animateEnemyHitExplosion2D(screenX, screenY, opts = {}) {
  if (typeof document === 'undefined') return null;
  const emitter = _ensureHitEmitter(opts);

  gsap.set(emitter.container, { x: screenX, y: screenY });
  for (const dot of emitter.dots) _resetDotStyles(dot, emitter.conf);

  emitter.tl.invalidate().restart(true, false);
  return emitter.tl;
}

export function animateSpawnIntro(group) {
  if (!group) return null;
  gsap.killTweensOf(group.scale);
  gsap.killTweensOf(group.position);
  const enemyRef = group.userData?.enemyRef || null;
  if (enemyRef) gsap.killTweensOf(enemyRef);
  const baseY = group.position.y;
  const spawnDropHeight = 14.0;
  group.scale.set(0.88, 0.88, 0.88);
  if (enemyRef) {
    enemyRef._spawnOffsetY = spawnDropHeight;
  } else {
    group.position.y = baseY + spawnDropHeight;
  }
  return gsap.timeline({ defaults: { overwrite: 'auto' } })
    .to(
      enemyRef || group.position,
      enemyRef
        ? { _spawnOffsetY: 0, duration: 0.62, ease: 'power3.in' }
        : { y: baseY, duration: 0.62, ease: 'power3.in' },
      0,
    )
    .to(group.scale, { x: 1.12, y: 0.9, z: 1.12, duration: 0.12, ease: 'power2.out' }, 0.52)
    .to(group.scale, { x: 1, y: 1, z: 1, duration: 0.2, ease: 'back.out(1.8)' }, 0.64);
}

export function animateEnemyHit(group, bodyMaterial) {
  if (!group) return null;
  gsap.killTweensOf(group.scale);
  const tl = gsap.timeline({ defaults: { overwrite: 'auto' } })
    .to(group.scale, { x: 1.07, y: 0.94, z: 1.07, duration: 0.06, ease: 'power1.out' })
    .to(group.scale, { x: 1, y: 1, z: 1, duration: 0.11, ease: 'power1.out' });
  if (bodyMaterial?.emissive) {
    gsap.killTweensOf(bodyMaterial);
    tl.to(bodyMaterial, { emissiveIntensity: 0.8, duration: 0.05, ease: 'power1.out' }, 0)
      .to(bodyMaterial, { emissiveIntensity: 0, duration: 0.14, ease: 'power1.in' }, 0.05);
  }
  return tl;
}

export function animateEnemyDeath(group, onDone) {
  if (!group) {
    onDone?.();
    return null;
  }
  gsap.killTweensOf(group.scale);
  return gsap.timeline({
    defaults: { overwrite: 'auto' },
    onComplete: () => onDone?.(),
  })
    .to(group.scale, { x: 1.12, y: 0.9, z: 1.12, duration: 0.08, ease: 'power1.out' })
    .to(group.scale, { x: 0.01, y: 0.01, z: 0.01, duration: 0.14, ease: 'power2.in' });
}

function toTargetMeshes(meshOrMeshes) {
  if (!meshOrMeshes) return [];
  return Array.isArray(meshOrMeshes) ? meshOrMeshes : [meshOrMeshes];
}

export function animateEnemyOutlinePopup(meshOrMeshes, material, opts = {}) {
  const meshes = toTargetMeshes(meshOrMeshes);
  if (meshes.length === 0 || !material) return null;
  const { intensity = 1 } = opts;
  const finalOpacity = 0.62 + intensity * 0.28;
  for (const m of meshes) gsap.killTweensOf(m.scale);
  gsap.killTweensOf(material);
  for (const m of meshes) {
    const baseScale = Number(m?.userData?.outlineBaseScale) > 0 ? Number(m.userData.outlineBaseScale) : 1;
    m.visible = true;
    m.scale.setScalar(baseScale * 0.35);
  }
  material.opacity = 0;
  const tl = gsap.timeline({ defaults: { overwrite: 'auto' } })
    .to(material, {
      opacity: 0.92,
      duration: 0.12,
      ease: 'power2.out',
    }, 0);
  for (const m of meshes) {
    const baseScale = Number(m?.userData?.outlineBaseScale) > 0 ? Number(m.userData.outlineBaseScale) : 1;
    tl.to(m.scale, {
      x: baseScale * (1.15 + intensity * 0.05),
      y: baseScale * (1.15 + intensity * 0.05),
      z: baseScale * (1.15 + intensity * 0.05),
      duration: 0.12,
      ease: 'back.out(3.5)',
    }, 0)
      .to(m.scale, {
        x: baseScale,
        y: baseScale,
        z: baseScale,
        duration: 0.2,
        ease: 'power2.out',
      }, 0.12);
  }
  return tl.to(material, {
    opacity: finalOpacity,
    duration: 0.18,
    ease: 'power1.out',
  }, 0.2);
}

export function animateEnemyOutlineHide(meshOrMeshes, material, onDone) {
  const meshes = toTargetMeshes(meshOrMeshes);
  if (meshes.length === 0 || !material) {
    onDone?.();
    return null;
  }
  for (const m of meshes) gsap.killTweensOf(m.scale);
  gsap.killTweensOf(material);
  const tl = gsap.timeline({
    defaults: { overwrite: 'auto' },
  });
  for (const m of meshes) {
    const baseScale = Number(m?.userData?.outlineBaseScale) > 0 ? Number(m.userData.outlineBaseScale) : 1;
    tl.to(m.scale, {
      x: baseScale * 0.84,
      y: baseScale * 0.84,
      z: baseScale * 0.84,
      duration: 0.14,
      ease: 'power1.in',
    }, 0);
  }
  return tl
    .to(material, {
      opacity: 0,
      duration: 0.14,
      ease: 'power1.in',
      onComplete: () => {
        for (const m of meshes) m.visible = false;
        onDone?.();
      },
    }, 0);
}

export function killObjectTweens(object) {
  if (!object) return;
  gsap.killTweensOf(object);
  if (object.position) gsap.killTweensOf(object.position);
  if (object.scale) gsap.killTweensOf(object.scale);
  if (object.rotation) gsap.killTweensOf(object.rotation);
  if (object.material) gsap.killTweensOf(object.material);
}

export function killTweensOf(target) {
  if (!target) return;
  gsap.killTweensOf(target);
}
